import { parsePhoneNumber } from '@/helper/phoneUtils';
import { showToast } from '@/helper/toast.helper';
import { useDebounce } from '@/hooks/useDebounce';
import { AppDispatch } from '@/store';
import { addUserToProject, verifyMobileUser } from '@/store/slices/projectsSlice/projectsSlice';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface AddUserToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

interface UserData {
  phoneNumber: string;
  blueNumberId: string;
  userId: number | null;
}

interface LoadingState {
  phone: boolean;
  blueNumber: boolean;
  submit: boolean;
}

interface ErrorState {
  phone: string;
  blueNumber: string;
}

const AddUserToProjectModal: React.FC<AddUserToProjectModalProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [userData, setUserData] = useState<UserData>({
    phoneNumber: '',
    blueNumberId: '',
    userId: null,
  });

  const [loadingStates, setLoadingStates] = useState<LoadingState>({
    phone: false,
    blueNumber: false,
    submit: false,
  });

  const [errorStates, setErrorStates] = useState<ErrorState>({
    phone: '',
    blueNumber: '',
  });

  const [isValidated, setIsValidated] = useState({
    phone: false,
    blueNumber: false,
  });

  const debouncedPhone = useDebounce(userData.phoneNumber, 500);
  const debouncedBlueNumber = useDebounce(userData.blueNumberId, 500);

  useEffect(() => {
    if (debouncedPhone && isOpen) {
      handlePhoneBlur(debouncedPhone);
    }
  }, [debouncedPhone]);

  useEffect(() => {
    if (debouncedBlueNumber && isOpen) {
      handleBlueNumberBlur(debouncedBlueNumber);
    }
  }, [debouncedBlueNumber]);

  useEffect(() => {
    if (!isOpen) {
      setUserData({ phoneNumber: '', blueNumberId: '', userId: null });
      setErrorStates({ phone: '', blueNumber: '' });
      setIsValidated({ phone: false, blueNumber: false });
      setLoadingStates({ phone: false, blueNumber: false, submit: false });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const setLoading = (field: 'phone' | 'blueNumber' | 'submit', value: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setError = (field: 'phone' | 'blueNumber', message: string) => {
    setErrorStates((prev) => ({
      ...prev,
      [field]: message,
    }));
  };

  const clearError = (field: 'phone' | 'blueNumber') => {
    setError(field, '');
  };

  const setValidation = (field: 'phone' | 'blueNumber', isValid: boolean) => {
    setIsValidated((prev) => ({
      ...prev,
      [field]: isValid,
    }));
  };

  const handlePhoneBlur = async (phone: string) => {
    const trimmedPhone = phone.trim();

    if (!trimmedPhone) {
      setValidation('phone', false);
      setLoading('phone', false);
      return;
    }

    if (!trimmedPhone.startsWith('+')) {
      setError('phone', 'Phone number must start with country code (e.g., +1, +91)');
      setValidation('phone', false);
      setLoading('phone', false);
      return;
    }

    const { countryCode, phoneNumber } = parsePhoneNumber(trimmedPhone);

    if (!phoneNumber || phoneNumber.length < 7) {
      setError('phone', 'Invalid phone number');
      setValidation('phone', false);
      setLoading('phone', false);
      return;
    }

    clearError('phone');
    setLoading('phone', true);
    setValidation('phone', false);

    try {
      const res = await dispatch(verifyMobileUser({ phoneNumber, countryCode, blueNumberId: '' }));

      if (verifyMobileUser.fulfilled.match(res)) {
        showToast.success('Phone number verified successfully');
        const data = res.payload.data;

        if (data?.blueNumberId) {
          setUserData((prev) => ({
            ...prev,
            blueNumberId: data.blueNumberId,
            userId: data.userId || data.id || null,
          }));
          clearError('phone');
          setValidation('phone', true);
          setValidation('blueNumber', true);
        } else {
          setUserData((prev) => ({ ...prev, blueNumberId: '' }));
          setError('phone', 'User not found');
          setValidation('phone', false);
        }
      } else if (verifyMobileUser.rejected.match(res)) {
        setUserData((prev) => ({ ...prev, blueNumberId: '' }));
        setError('phone', 'User not found');
        setValidation('phone', false);
      }
    } catch (error) {
      setUserData((prev) => ({ ...prev, blueNumberId: '' }));
      setError('phone', 'User not found');
      setValidation('phone', false);
    } finally {
      setLoading('phone', false);
    }
  };

  const handleBlueNumberBlur = async (blueId: string) => {
    if (!blueId) {
      setValidation('blueNumber', false);
      setLoading('blueNumber', false);
      return;
    }

    clearError('blueNumber');
    setLoading('blueNumber', true);
    setValidation('blueNumber', false);

    try {
      const res = await dispatch(
        verifyMobileUser({ phoneNumber: '', countryCode: '', blueNumberId: blueId }),
      );

      if (verifyMobileUser.fulfilled.match(res)) {
        const data = res.payload.data;

        if (data?.phoneNumber && data?.countryCode) {
          const fullPhone = `${data.countryCode}${data.phoneNumber}`;
          setUserData((prev) => ({
            ...prev,
            phoneNumber: fullPhone,
            userId: data.userId || data.id || null,
          }));
          clearError('blueNumber');
          setValidation('blueNumber', true);
          setValidation('phone', true);
        } else {
          setUserData((prev) => ({ ...prev, phoneNumber: '' }));
          setError('blueNumber', 'User not found');
          setValidation('blueNumber', false);
        }
      } else if (verifyMobileUser.rejected.match(res)) {
        setUserData((prev) => ({ ...prev, phoneNumber: '' }));
        setError('blueNumber', 'User not found');
        setValidation('blueNumber', false);
      }
    } catch (error) {
      setUserData((prev) => ({ ...prev, phoneNumber: '' }));
      setError('blueNumber', 'User not found');
      setValidation('blueNumber', false);
    } finally {
      setLoading('blueNumber', false);
    }
  };

  const handlePhoneChange = (value: string) => {
    setUserData((prev) => ({ ...prev, phoneNumber: value }));
    clearError('phone');
    setValidation('phone', false);
    if (value.trim()) {
      setLoading('phone', true);
    }
  };

  const handleBlueNumberChange = (value: string) => {
    setUserData((prev) => ({ ...prev, blueNumberId: value }));
    clearError('blueNumber');
    setValidation('blueNumber', false);
    if (value.trim()) {
      setLoading('blueNumber', true);
    }
  };

  const handleCreateUser = async () => {
    // Validate that both fields are filled

    if (!userData.phoneNumber || !userData.blueNumberId) {
      showToast.error('Please fill in both phone number and User Name');
      return;
    }

    // Validate that both fields are validated
    if (!isValidated.phone || !isValidated.blueNumber) {
      showToast.error('Please ensure both fields are validated');
      return;
    }

    setLoading('submit', true);

    if (!userData.userId) {
      showToast.error('User ID not found');
      return;
    }

    try {
      await dispatch(
        addUserToProject({
          projectId,
          phoneNumber: userData.phoneNumber,
          blueNumberId: userData.blueNumberId,
          userId: userData.userId,
        }),
      ).unwrap();

      showToast.success('User added to project successfully');
      handleClose();
    } catch (error) {
      showToast.error((error as string) || 'Error adding user to project');
    } finally {
      setLoading('submit', false);
    }
  };

  const handleClose = () => {
    // Reset all states
    setUserData({ phoneNumber: '', blueNumberId: '', userId: null });
    setErrorStates({ phone: '', blueNumber: '' });
    setIsValidated({ phone: false, blueNumber: false });
    setLoadingStates({ phone: false, blueNumber: false, submit: false });
    onClose();
  };

  return (
    <div
      className="fixed left-[255px] top-[72px] right-0 bottom-0 z-40 flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-[#621C43] rounded-lg shadow-lg w-[90%] max-w-[320px] relative text-white">
        {/* Header */}
        <div className="flex justify-between px-6 py-4 border-b border-[#E0C9D8] items-center">
          <h2 className="text-lg font-bold">Add Project Participant</h2>
          <button
            className="text-[#FFF8ED] hover:text-gray-300 transition-colors"
            onClick={handleClose}
            disabled={loadingStates.submit}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <div className="space-y-3 p-6 py-3">
          {/* UserName           No need to display Blue Number field as per new requirements (By Harish) */}

          {/* <div>                                               
            <label className="block text-sm mb-1">
              User Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={userData.blueNumberId}
                onChange={(e) => handleBlueNumberChange(e.target.value)}
                placeholder="Enter the Username number"
                className={`w-full p-1.5 px-3 rounded-lg text-[#fff] placeholder-[#fff] text-sm bg-[#824865] focus:outline-none ${
                  errorStates.blueNumber ? 'border-red-500 border' : ''
                }`}
                disabled={loadingStates.submit}
              />
              {loadingStates.blueNumber && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#fff]" />
                </div>
              )}
            </div>
            {errorStates.blueNumber && (
              <p className="text-xs text-red-300 mt-1">{errorStates.blueNumber}</p>
            )}
          </div> */}
          {/* Mobile Number */}
          <div>
            <label className="block text-sm mb-1">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={userData.phoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="+91xxxxxxxxxx"
                className={`w-full p-1.5 px-3 rounded-lg text-[#fff] placeholder-[#fff] text-sm bg-[#824865] focus:outline-none ${
                  errorStates.phone ? 'border-red-500 border' : ''
                }`}
                disabled={loadingStates.submit}
              />
              {loadingStates.phone && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#fff]" />
                </div>
              )}
            </div>
            {errorStates.phone && <p className="text-xs text-red-300 mt-1">{errorStates.phone}</p>}
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleCreateUser}
              disabled={
                loadingStates.submit ||
                !isValidated.phone ||
                !isValidated.blueNumber ||
                !userData.phoneNumber ||
                !userData.blueNumberId
              }
              className="bg-green-500 mt-1 hover:bg-green-600 text-white text-sm px-8 py-1 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingStates.submit && <Loader2 className="w-4 h-4 animate-spin" />}
              Create User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserToProjectModal;
